import { Trans, useLingui } from '@lingui/react/macro'
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { useAppForm } from '@/lib/use-app-form'
import { useMutation } from '@tanstack/react-query'
import { trpc } from '@/main'
import * as z from 'zod'
import { toast } from '@/lib/toast'

export function ContactDialog(props: { openContact: boolean; setOpenContact: (value: boolean) => void }) {
  const { t } = useLingui()
  const { mutateAsync: sendContactForm, isSuccess } = useMutation(trpc.common.sendContactForm.mutationOptions())

  const form = useAppForm({
    validators: {
      onChange: z.object({
        name: z.string().min(1),
        email: z.email(),
        message: z.string().min(1),
        terms: z.boolean().refine((val) => val, t`You must accept the privacy policy and terms of use`),
      }),
    },
    defaultValues: {
      name: '',
      email: '',
      message: '',
      terms: false,
    },
    onSubmit: async ({ value }) => {
      await sendContactForm(value)
      toast.success(t`Message sent successfully`)
    },
  })

  return (
    <Dialog open={props.openContact} onOpenChange={props.setOpenContact}>
      <DialogContent aria-describedby={undefined} className="max-w-[480px]">
        <DialogHeader>
          <DialogTitle className="text-center text-2xl font-semibold">{t`Contact us`}</DialogTitle>
        </DialogHeader>

        <form
          onSubmit={(event) => {
            event.preventDefault()
            form.handleSubmit()
          }}
          className="flex flex-col gap-3"
        >
          <form.AppField name="name" children={(field) => <field.TextField label={t`Name`} required />} />

          <form.AppField
            name="email"
            children={(field) => <field.TextField label={t`Email`} type="email" required />}
          />

          <form.AppField name="message" children={(field) => <field.TextareaField label={t`Message`} required />} />

          <form.AppField
            name="terms"
            children={(field) => (
              <field.CheckboxField
                label={
                  <div>
                    <Trans>
                      I accept the{' '}
                      <a
                        href="/terms-of-use"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        Terms of use
                      </a>{' '}
                      and the{' '}
                      <a
                        href="/privacy-policy"
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-primary hover:underline"
                      >
                        Privacy Policy
                      </a>
                    </Trans>
                  </div>
                }
              />
            )}
          />

          <form.AppForm>
            <form.SubmitButton label={t`Send`} disabled={isSuccess} className="w-full" />
          </form.AppForm>
        </form>
      </DialogContent>
    </Dialog>
  )
}
